import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Search, Filter } from 'lucide-react';
import { Store } from '../../services/store';
import { ProductFilters as FilterType } from '../../services/product';

interface ProductFiltersProps {
  stores: Store[];
  categories: string[];
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
}

export function ProductFilters({ 
  stores, 
  categories, 
  filters, 
  onFiltersChange
}: ProductFiltersProps) {
  const [localFilters, setLocalFilters] = useState<FilterType>(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Update local filters when props change (e.g., when category is reset due to store change)
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleInputChange = (field: keyof FilterType, value: any) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
  };

  const handleSearch = () => {
    onFiltersChange(localFilters);
  };

  const handleReset = () => {
    const resetFilters: FilterType = { page: 1, limit: 20 };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  // Check if any advanced filters are active
  const hasActiveAdvancedFilters = () => {
    return (
      localFilters.has_images !== undefined ||
      localFilters.stock_status !== undefined ||
      (localFilters.limit && localFilters.limit !== 20)
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Card className="card-modern animate-slide-up">
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Primary filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-slide-up" style={{animationDelay: '50ms'}}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search products..."
                value={localFilters.search || ''}
                onChange={(e) => handleInputChange('search', e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10 h-11"
              />
            </div>

            <Select
              value={localFilters.store_id || 'all'}
              onValueChange={(value) => {
                const newStoreId = value === 'all' ? '' : value;
                // Reset category when store changes
                const newFilters = { ...localFilters, store_id: newStoreId, category: undefined };
                setLocalFilters(newFilters);
                // Trigger immediate search to update categories
                onFiltersChange(newFilters);
              }}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="All stores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                {stores.map((store) => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={localFilters.category || 'all'}
              onValueChange={(value) => handleInputChange('category', value === 'all' ? '' : value)}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button onClick={handleSearch} className="flex-1 h-11 shadow-md">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="px-3 h-11 relative"
              >
                <Filter className="w-4 h-4" />
                {hasActiveAdvancedFilters() && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full border-2 border-white shadow-sm">
                    <span className="sr-only">Active filters</span>
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Advanced filters */}
          {showAdvanced && (
            <div className="border-t border-border/50 pt-6 space-y-6">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-primary" />
                <h4 className="text-sm font-semibold">Advanced Filters</h4>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Image filters */}
                <div className="space-y-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Image Status</div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="has-images"
                        checked={localFilters.has_images === true}
                        onCheckedChange={(checked) =>
                          handleInputChange('has_images', checked ? true : undefined)
                        }
                      />
                      <label htmlFor="has-images" className="text-sm font-medium cursor-pointer">
                        Has images only
                      </label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="no-images"
                        checked={localFilters.has_images === false}
                        onCheckedChange={(checked) =>
                          handleInputChange('has_images', checked ? false : undefined)
                        }
                      />
                      <label htmlFor="no-images" className="text-sm font-medium cursor-pointer">
                        No images only
                      </label>
                    </div>
                  </div>
                </div>

                {/* Stock filters */}
                <div className="space-y-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Stock Status</div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="in-stock"
                        checked={localFilters.stock_status === 'in_stock'}
                        onCheckedChange={(checked) =>
                          handleInputChange('stock_status', checked ? 'in_stock' : undefined)
                        }
                      />
                      <label htmlFor="in-stock" className="text-sm font-medium cursor-pointer">
                        In stock only
                      </label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="out-of-stock"
                        checked={localFilters.stock_status === 'out_of_stock'}
                        onCheckedChange={(checked) =>
                          handleInputChange('stock_status', checked ? 'out_of_stock' : undefined)
                        }
                      />
                      <label htmlFor="out-of-stock" className="text-sm font-medium cursor-pointer">
                        Out of stock only
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items per page */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Select
                  value={localFilters.limit?.toString() || '20'}
                  onValueChange={(value) => handleInputChange('limit', parseInt(value))}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSearch} size="sm" className="shadow-md">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={handleReset} size="sm">
                  Reset All
                </Button>
              </div>
            </div>
          )}

        </div>
      </CardContent>
    </Card>
  );
}